import React from 'react';
import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import {
  PRODUCT_UNITS,
  POPULAR_UNIT_CODES,
  normalizeUnitCode,
  findUnitDef,
  formatUnitDisplay,
} from '../../../constants/productUnits';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  chipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 8,
  },
  quickChip: {
    fontWeight: 600,
    fontSize: '0.75rem',
    borderRadius: 6,
    cursor: 'pointer',
    transition: 'all 0.15s ease-in-out',
  },
  chipSelected: {
    backgroundColor: '#17497F !important',
    color: '#ffffff !important',
    boxShadow: '0 2px 4px rgba(23, 73, 127, 0.25)',
  },
  chipUnselected: {
    backgroundColor: '#f1f5f9',
    color: '#334155',
    border: '1px solid #e2e8f0',
    '&:hover': {
      backgroundColor: '#e2e8f0',
    },
  },
  categoryHeader: {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '4px 8px',
    backgroundColor: '#f8fafc',
  },
}));

/**
 * Componente Reutilizável de Seleção de Unidade de Medida com UI e UX otimizadas.
 *
 * Props:
 * - value: string (ex: 'UN', 'KG', 'L')
 * - onChange: (unitCode: string) => void
 * - label: string (default: 'Unidade de Medida')
 * - showQuickChips: boolean (default: true)
 * - variant: 'standard' | 'compact' | 'chips-only' (default: 'standard')
 * - size: 'small' | 'medium' (default: 'small')
 * - disabled: boolean (default: false)
 * - error: boolean
 * - helperText: string
 * - field, form: suporte nativo para Formik <Field component={ProductUnitSelect} />
 */
const ProductUnitSelect = ({
  value: propValue,
  onChange: propOnChange,
  label = 'Unidade de Medida',
  showQuickChips = true,
  variant = 'standard',
  size = 'small',
  disabled = false,
  error = false,
  helperText,
  field,
  form,
  ...rest
}) => {
  const classes = useStyles();

  // Integração transparente com Formik se fornecido via <Field component={...} />
  const currentValue = normalizeUnitCode(
    field ? field.value : propValue || 'UN'
  );

  const handleSelectUnit = (newUnit) => {
    const normalized = normalizeUnitCode(newUnit);
    if (field && form) {
      form.setFieldValue(field.name, normalized);
    } else if (propOnChange) {
      propOnChange(normalized);
    }
  };

  const selectedDef = findUnitDef(currentValue);

  // Modo Apenas Chips (para cartões de ação, duxIA, modais compactos)
  if (variant === 'chips-only') {
    return (
      <Box className={classes.chipsContainer}>
        {POPULAR_UNIT_CODES.map((code) => {
          const isSelected = currentValue === code;
          return (
            <Chip
              key={code}
              label={code}
              size={size}
              disabled={disabled}
              className={`${classes.quickChip} ${
                isSelected ? classes.chipSelected : classes.chipUnselected
              }`}
              onClick={() => handleSelectUnit(code)}
            />
          );
        })}
      </Box>
    );
  }

  // Modo Compacto (Select simples para tabelas e formulários densos)
  if (variant === 'compact') {
    return (
      <FormControl
        variant="outlined"
        size={size}
        fullWidth
        disabled={disabled}
        error={error}
        {...rest}
      >
        {label && <InputLabel id="unit-select-compact-label">{label}</InputLabel>}
        <Select
          labelId="unit-select-compact-label"
          label={label}
          value={currentValue}
          onChange={(e) => handleSelectUnit(e.target.value)}
        >
          {PRODUCT_UNITS.map((u) => (
            <MenuItem key={u.code} value={u.code}>
              <strong>{u.code}</strong> - {u.label}
            </MenuItem>
          ))}
          {!PRODUCT_UNITS.some((u) => u.code === currentValue) && (
            <MenuItem value={currentValue}>
              <strong>{currentValue}</strong> (Personalizado)
            </MenuItem>
          )}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }

  // Modo Padrão: Chips Rápidos no topo + Autocomplete pesquisável
  return (
    <Box className={classes.root}>
      {showQuickChips && (
        <Box mb={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="caption" style={{ fontWeight: 600, color: '#475569' }}>
              Atalhos Rápidos:
            </Typography>
            <Typography variant="caption" style={{ color: '#0d47a1', fontWeight: 600 }}>
              Ativo: {selectedDef.code} ({selectedDef.label})
            </Typography>
          </Box>
          <Box className={classes.chipsContainer}>
            {POPULAR_UNIT_CODES.map((code) => {
              const isSelected = currentValue === code;
              return (
                <Chip
                  key={code}
                  label={code}
                  size="small"
                  disabled={disabled}
                  className={`${classes.quickChip} ${
                    isSelected ? classes.chipSelected : classes.chipUnselected
                  }`}
                  onClick={() => handleSelectUnit(code)}
                />
              );
            })}
          </Box>
        </Box>
      )}

      <Autocomplete
        freeSolo
        disableClearable
        disabled={disabled}
        options={PRODUCT_UNITS}
        groupBy={(option) => (typeof option === 'string' ? 'Outros' : option.category)}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option.toUpperCase();
          return `${option.code} - ${option.label}`;
        }}
        value={selectedDef}
        onChange={(event, newValue) => {
          if (!newValue) return;
          if (typeof newValue === 'string') {
            handleSelectUnit(newValue);
          } else if (newValue.code) {
            handleSelectUnit(newValue.code);
          }
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === 'reset' && newInputValue) {
            return;
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            size={size}
            error={error}
            helperText={helperText || 'Selecione ou digite um código de unidade'}
            placeholder="Ex: UN, KG, L, CX, M"
            inputProps={{
              ...params.inputProps,
              style: { textTransform: 'uppercase' },
            }}
          />
        )}
      />
    </Box>
  );
};

export default ProductUnitSelect;

